import React from 'react';
import { matchPath } from 'react-router-dom';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import PersonIcon from '@mui/icons-material/Person';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import SavingsIcon from '@mui/icons-material/Savings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { DirectionsRun, Spa } from '@mui/icons-material';
import { ROUTES } from '../../utils';

export type NavigationItem = {
  text: string;
  icon: React.ReactElement;
  path: string;
  dashboardKey?: string;
  /** Only visible to central admins (e.g. Fatigue Management). */
  centralOnly?: boolean;
};

export type NavigationSection = {
  category: string;
  items: NavigationItem[];
};

/**
 * Single source of truth for admin navigation. Consumed by AdminSideNav
 * (rendering + active highlighting) and AdminAppHeader (page title), so the
 * two can never drift apart again.
 */
export const adminNavigationSections: NavigationSection[] = [
  {
    category: 'WELLNESS & HEALTH',
    items: [
      {
        text: 'Programme Profile',
        icon: <SelfImprovementIcon fontSize='medium' />,
        path: ROUTES.ADMIN_DASHBOARD,
        dashboardKey: 'programme_profile',
      },
      {
        text: 'Wellness Overview',
        icon: <InsightsIcon fontSize='medium' />,
        path: '/admin/wellness-overview',
        dashboardKey: 'wellness_overview',
      },
      {
        text: 'Employee Profile Comparison',
        icon: <PersonIcon fontSize='medium' />,
        path: '/admin/employee-profile-comparison',
        dashboardKey: 'employee_profile_comparison',
      },
      {
        text: 'Recovery',
        icon: <SelfImprovementIcon fontSize='medium' />,
        path: '/admin/recovery',
        dashboardKey: 'recovery',
      },
      {
        text: 'Sleep Analytics',
        icon: <BedtimeIcon fontSize='medium' />,
        path: '/admin/sleep-analytics',
        dashboardKey: 'sleep_analytics',
      },
      {
        text: 'Strain and Activity',
        icon: <DirectionsRun fontSize='medium' />,
        path: '/admin/strain-activity',
        dashboardKey: 'strain_activity',
      },
      {
        text: 'Overall Physiological Stress',
        icon: <Spa fontSize='medium' />,
        path: '/admin/stress-mind',
        dashboardKey: 'overall_stress_mind',
      },
      {
        text: 'Biometrics and Vitals',
        icon: <MonitorHeartIcon fontSize='medium' />,
        path: '/admin/biometrics',
        dashboardKey: 'biometrics_vitals',
      },
      {
        text: 'User Progress',
        icon: <TrendingUpIcon fontSize='medium' />,
        path: '/admin/user-progress',
        dashboardKey: 'user_progress',
      },
      {
        text: 'Team Progress',
        icon: <BusinessIcon fontSize='medium' />,
        path: '/admin/team-progress',
        dashboardKey: 'team_progress',
      },
      {
        text: 'Jeopardy Insights',
        icon: <WarningIcon fontSize='medium' />,
        path: '/admin/jeopardy-insights',
        dashboardKey: 'jeopardy_insights',
      },
      {
        text: 'Measure Comparison Insights',
        icon: <CompareArrowsIcon fontSize='medium' />,
        path: '/admin/measure-comparison',
        dashboardKey: 'measure_comparison',
      },
      {
        text: 'Predictive Insights',
        icon: <PsychologyIcon fontSize='medium' />,
        path: '/admin/predictive-insights',
        dashboardKey: 'predictive_insights',
      },
      {
        text: 'Fatigue Management',
        icon: <BatteryAlertIcon fontSize='medium' />,
        path: ROUTES.FATIGUE_MANAGEMENT,
        centralOnly: true,
      },
      {
        text: 'ROI Reporting',
        icon: <SavingsIcon fontSize='medium' />,
        path: ROUTES.ROI_REPORTING,
        centralOnly: true,
      },
      {
        text: 'Reporting',
        icon: <AssessmentIcon fontSize='medium' />,
        path: ROUTES.REPORTING,
        centralOnly: true,
      },
    ],
  },
  /*
  {
    category: "WELLNESS PROGRAMS",
    items: [
      {
        text: "Learning Resources",
        icon: <MenuBookIcon fontSize="medium" />,
        path: "/admin/learning-resources",
      },
      {
        text: "Certificates & Badges",
        icon: <WorkspacePremiumIcon fontSize="medium" />,
        path: "/admin/certificates-badges",
      },
      {
        text: "Programs & Challenges",
        icon: <EmojiEventsIcon fontSize="medium" />,
        path: "/admin/programs-challenges",
      },
    ],
  },
  */
  {
    category: 'ADMINISTRATION',
    items: [
      {
        text: 'Settings',
        icon: <SettingsIcon fontSize='medium' />,
        path: ROUTES.SETTINGS,
      },
    ],
  },
];

/**
 * True when pathname is the item's path or any deeper route under it
 * (e.g. /admin/settings/clients/123 → Settings, /admin/reporting/42 →
 * Reporting). matchPath is segment-based, so /admin/roi-reporting never
 * matches /admin/reporting.
 */
export const isNavItemActive = (pathname: string, item: NavigationItem): boolean =>
  !!matchPath({ path: item.path, end: true }, pathname) || !!matchPath({ path: `${item.path}/*`, end: true }, pathname);

export const findActiveNavItem = (pathname: string, items: NavigationItem[]): NavigationItem | undefined =>
  items.find((item) => isNavItemActive(pathname, item));
